import { useState, useCallback } from "react";
import { FunctionsHttpError } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";

/** Erros possiveis ao iniciar Checkout ou abrir o Portal, ja em pt-BR. */
const CHECKOUT_ERROR = "Não foi possível iniciar o checkout. Tente novamente.";
const PORTAL_ERROR = "Não foi possível abrir o gerenciamento de assinatura.";
const PORTAL_NO_SUB_ERROR =
  "Você ainda não tem uma assinatura ativa para gerenciar.";

interface InvokeResult {
  url?: string;
  error?: string;
}

/** Redireciona o navegador para a URL retornada pela Edge Function. Isolado para
 *  permitir override em testes (jsdom nao implementa navegacao real). */
function redirectTo(url: string): void {
  window.location.assign(url);
}

/** Le o campo `error` do corpo de uma resposta de erro HTTP da Edge Function.
 *  O supabase-js lanca FunctionsHttpError em status >= 400, com a Response em
 *  `context`; o corpo JSON traz o codigo (ex.: "no_active_subscription"). */
async function readErrorCode(invokeError: unknown): Promise<string | null> {
  if (!(invokeError instanceof FunctionsHttpError)) {
    return null;
  }
  try {
    const body = (await invokeError.context.json()) as InvokeResult;
    return body.error ?? null;
  } catch {
    return null;
  }
}

export interface UseBillingResult {
  /** Inicia o Checkout do Price escolhido e redireciona para o Stripe. */
  startCheckout: (priceId: string) => Promise<void>;
  /** Abre o Customer Portal e redireciona para o Stripe. */
  openPortal: () => Promise<void>;
  /** true enquanto uma das acoes esta em andamento (desabilita os CTAs). */
  loading: boolean;
  /** Mensagem de erro da ultima acao, em pt-BR. null quando nao ha erro. */
  error: string | null;
}

/** Hook de billing do painel: dispara Checkout e Portal via Edge Functions.
 *  As funcoes invocadas recebem o JWT do usuario automaticamente pelo client
 *  Supabase autenticado, e respondem { url } para redirect. */
export function useBilling(): UseBillingResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startCheckout = useCallback(async (priceId: string): Promise<void> => {
    setLoading(true);
    setError(null);

    const { data, error: invokeError } = await supabase.functions.invoke<InvokeResult>(
      "billing-checkout",
      { body: { priceId } },
    );

    if (invokeError || !data?.url) {
      setError(CHECKOUT_ERROR);
      setLoading(false);
      return;
    }

    redirectTo(data.url);
    // Nao limpa loading: a navegacao para o Stripe assume a partir daqui.
  }, []);

  const openPortal = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);

    const { data, error: invokeError } = await supabase.functions.invoke<InvokeResult>(
      "billing-portal",
      { body: {} },
    );

    if (invokeError || !data?.url) {
      // A function responde 409 quando o tenant nunca assinou; diferencia a mensagem
      // lendo o codigo de erro do corpo da resposta HTTP.
      const code = await readErrorCode(invokeError);
      const message = code === "no_active_subscription"
        ? PORTAL_NO_SUB_ERROR
        : PORTAL_ERROR;
      setError(message);
      setLoading(false);
      return;
    }

    redirectTo(data.url);
  }, []);

  return { startCheckout, openPortal, loading, error };
}
